import { TokenKind } from "common/records/Token";
import { AclKeys, AclValues } from "common/records/User";
import { RootState } from "common/reducers";
import { isI360Lightning } from "common/selectors/token.selectors";

export type CategoryKind =
  | "merchant_services"
  | "crm"
  | "financing"
  | "measurement_providers"
  | "presentation"
  | "estimation"
  | "design_tools"
  | "other";

export interface IntegrationsProps {
  name: string;
  category: CategoryKind;
  description: string;
  about?: string;
  capabilities?: string;
  crmUsersModelName?: string;
  crmUsersEditDisabled?: boolean;
  infoUrl?: string;
  secondaryCrmUserSelector?: (state: RootState) => boolean;
  secondaryCrmUsersModelName?: string;
  resource?: AclKeys;
  permission?: AclValues;
}

export const integrationsData: Partial<{ [key in TokenKind]: IntegrationsProps }> = {
  one_look: {
    name: "1LOOK",
    category: "financing",
    description:
      "Universal app + multi-lender logic = faster approvals and higher close rates—built for contractors, not banks.",
  },
  occ_pay: {
    name: "One Click Payments",
    category: "merchant_services",
    description: "Accept payments in One Click Contractor",
  },
  one_click_crm: {
    name: "One Click CRM",
    category: "crm",
    description: "The only CRM built for contractors and fully integrated with One Click Contractor in real time.",
    capabilities:
      "* Capture & Convert: Bring in leads from social media, websites, and forms.\r\n* Stay on Track: Manage your sales and production pipelines with confidence.\r\n* Automate Your Workflow: Eliminate mistakes and save hours by automating tasks, communications, and follow-ups.\r\n* Schedule Smarter: Coordinate projects and teams with shared calendars.\r\n* Integrate Directly: Sync with QuickBooks for accurate financials—instantly.\r\n* See the Big Picture: Generate powerful reports for actionable insights.",
    about:
      "The only real-time CRM built exclusively for One Click Contractor, giving you a seamless connection between your sales process and production. No delays. No double entry. No missed steps.",
    crmUsersModelName: "occ_crm_users",
  },
  paysimple: {
    name: "PaySimple",
    category: "merchant_services",
    description: "Integrated payments to simplify and empower the lives of small business owners.",
    capabilities:
      "Easily receive payments from customers either in person or remotely. We support payments from either ACH or credit cards.",
  },
  marketsharp: {
    name: "MarketSharp",
    category: "crm",
    description: "The All-in-One CRM Software for Contractors.",
    capabilities:
      "Integrate OCC with your Marketsharp CRM to provide a two-way integration to both get appointment/job/customer info into OCC as well as the ability to result appointments and attach executed agreements.",
    crmUsersModelName: "ms_users",
    infoUrl: "https://help.oneclickcontractor.com/knowledge/marketsharp-integration",
  },
  improveit360: {
    name: "Improveit360",
    category: "crm",
    description: "Enterprise software built for your home improvement business.",
    capabilities:
      "Integrate OCC with your ImproveIt 360 Lightning or Classic CRM to provide a two-way integration to both get appointment/job/customer info into OCC as well as the ability to result appointments and attach executed agreements. See more info for specific details on integration features.",
    infoUrl: "https://help.oneclickcontractor.com/knowledge/improveit-360-integration",
    crmUsersModelName: "i360_users",
    secondaryCrmUserSelector: isI360Lightning,
    secondaryCrmUsersModelName: "i360_lightning_users",
  },
  salesforce: {
    name: "Salesforce",
    category: "crm",
    description: "Unite marketing, sales, and service in a single app.",
    crmUsersModelName: "sf_users",
  },
  lead_perfection: {
    name: "LeadPerfection",
    category: "crm",
    description: "Feature by feature the best CRM Software for Remodelers since 2004.",
    capabilities:
      "Integrate OCC with your LeadPerfection to provide a two-way integration to both get appointment/job/customer info into OCC as well as the ability to result appointments and attach executed agreements. See more info for specific details on integration features.",
    infoUrl: "https://help.oneclickcontractor.com/knowledge/leadperfection-data-mapping",
    crmUsersModelName: "lp_users",
  },
  sales_rabbit: {
    name: "SalesRabbit",
    category: "crm",
    description: "Immediately improve results, morale, and team chemistry.",
    capabilities:
      "Integrate OCC with SalesRabbit to get canvassed lead data into OCC. See more info for specific details on integration features.",
    crmUsersModelName: "sr_users",
    infoUrl: "https://help.oneclickcontractor.com/knowledge/sales-rabbit-integration",
  },
  job_nimbus: {
    name: "JobNimbus",
    category: "crm",
    description:
      "JobNimbus has you covered as the best-in-class CRM for contractors, and is trusted by over 6,000 roofing business heroes—just like you.",
    capabilities:
      "Integrate OCC with your JobNimbus CRM to provide a two-way integration to both get appointment/job/customer info into OCC as well as the ability to result appointments and attach executed agreements. See more info for specific details on integration features.",
    crmUsersModelName: "jn_users",
    infoUrl: "https://help.oneclickcontractor.com/knowledge/setting-up-jobnimbus-and-one-click-contractor",
  },
  jobprogress: {
    name: "Job Progress",
    category: "crm",
    description: "",
    crmUsersModelName: "jp_users",
    infoUrl: "https://help.oneclickcontractor.com/knowledge/setting-up-jobprogress-in-one-click-contractor",
  },
  accu_lynx: {
    name: "AccuLynx",
    category: "crm",
    description: "AccuLynx is the #1 roofing software that helps contractors grow.",
    capabilities:
      "Integrate OCC with your AccuLynx CRM to provide a two-way integration to both get appointment/job/customer info into OCC as well as the ability to result appointments and attach executed agreements. See more info for specific details on integration features.",
    infoUrl: "https://help.oneclickcontractor.com/knowledge/setting-up-acculynx-in-one-click-contractor",
    crmUsersModelName: "al_users",
  },
  quick_measure: {
    name: "QuickMeasure",
    category: "measurement_providers",
    description: "Roof measurements in under an hour",
    capabilities:
      "You can seamlessly order a measurement report from QuickMeasure and receive the report and all measurement data automatically on our measurements tab.",
  },
  eagleview: {
    name: "EagleView",
    category: "measurement_providers",
    description: "Industry leaders trust EagleView to deliver, each and every time.",
    capabilities:
      "You can seamless order a measurement report from Eagleview and receive the report and all measurement data automatically on our measurements tab.",
    infoUrl: "https://help.oneclickcontractor.com/knowledge/authorizing-your-eagleview-account",
  },
  hover: {
    name: "HOVER",
    category: "measurement_providers",
    description: "Turn photos from your phone into fast and accurate measurements that save time and money.",
    capabilities:
      "Easily import and photos, reports, visualizations, and measurement data automatically on our measurements tab.",
    crmUsersModelName: "hover_users",
    infoUrl: "https://help.oneclickcontractor.com/knowledge/how-does-the-hover-integration-work",
  },
  roof_scope: {
    name: "ScopeTechnologies",
    category: "measurement_providers",
    description: "Roof Measurements in 3 easy steps.",
    capabilities:
      "You can seamless order a measurement report from Scope Technologies and receive the report and all measurement data automatically on our measurements tab.",
    infoUrl: "https://help.oneclickcontractor.com/knowledge/authorizing-your-roofscope-account-in-one-click-contractor",
  },
  ingage: {
    name: "Ingage",
    category: "presentation",
    description: "Close more deals with powerful presentations",
    capabilities:
      "With the integrated presentation capabilities you can present your price in a dynamic Ingage presentation.",
    infoUrl: "https://help.oneclickcontractor.com/knowledge/ingage-price-presentation-now-configurable",
  },
  fene_tech: {
    name: "FeneVision WEB",
    category: "estimation",
    description:
      "FeneVision WEB is a web based quotation and order entry software application for the window, door, sunroom and glass manufacturing industries.",
    capabilities:
      "With the FeneVision WEB integration, you can seamlessly import quote pricing and details, then combine them with other job or trade information to create professional estimates, proposals, and contracts.",
  },
  provia: {
    name: "ProVia",
    category: "estimation",
    description:
      "We combine old-world craftsmanship with state-of-the-art technology to deliver personalized care with every order.",
    capabilities:
      "With a ProVia entryLINK integration, you can import estimate data from entryLINK and combine that with other trades and easily send the agreement off for signature.",
    infoUrl: "https://help.oneclickcontractor.com/knowledge/how-do-i-integrate-provia",
  },
  renoworks: {
    name: "Renoworks",
    category: "design_tools",
    description: "Visualization Solutions built on data science and lead generation.",
    capabilities:
      "With a Renoworks account, you can seamlessly store your renoworks renderings as Visualizations in One Click. This allows you to keep them organized with the rest of your other sales collateral.",
  },
  companycam: {
    name: "CompanyCam",
    crmUsersModelName: "cc_users",
    category: "other",
    description: "Job site photo tools to manage work from anywhere.",
    capabilities:
      "Automatically create projects in CompanyCam from OCC or just pull in projects from CompanyCam into OCC. Regardless, you'll get all of the photos and documents uploaded to CompanyCam automatically synchronized back into OCC for reference and incorporation into your presentations or estimate/proposal.",
    infoUrl: "https://help.oneclickcontractor.com/knowledge/hooking-up-companycam-and-one-click-contractor",
  },
  home_advisor: {
    name: "Home Advisor",
    category: "other",
    description: "Replace",
  },
  angies_list: {
    name: "Angies List",
    category: "other",
    description: "Find top-rated certified pros in your area.",
  },
  houzz: {
    name: "Houzz",
    category: "other",
    description: "Thousands of homeowners connect with the right pro every week for their remodeling projects.",
  },
  service_minder: {
    name: "ServiceMinder",
    category: "crm",
    description: "World-class CRM built for field service with mapping, automated scheduling and dispatch.",
    capabilities: "Integrate OCC with ServiceMinder to get assigned Service Appointment data into OCC.",
    crmUsersModelName: "sm_users",
  },
};

export const integrationCategories = {
  all: "All",
  merchant_services: "Merchant Services",
  crm: "CRMs",
  financing: "Financing",
  measurement_providers: "Measurement Providers",
  presentation: "Presentations",
  estimation: "Estimation",
  design_tools: "Design Tools",
  other: "Others",
};
